import { ArrowUpDownIcon } from 'lucide-react';
import moment from 'moment';
import React, { useState } from 'react';
import { PaginationTable } from 'src/domains/casino/webview/bonus-engine/common/components/pagination-table/PaginationTable';
import { Button } from 'src/domains/common/components/button/button';
import { DatePicker } from 'src/domains/common/components/date-picker';
import { Dialog, DialogFooter, DialogHeader, DialogTitle } from 'src/domains/common/components/dialog/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from 'src/domains/common/components/table/table';
import { Checkbox } from 'src/domains/layouts/shared/checkbox';
import {
    BulkDeleteDialogState,
    SportEventModelType,
} from 'src/main/cms/PromotedEvents/bulkDeleteDialog/bulkDeleteDialogState';
import { observer } from 'src/utils/mobx-react';

type PropsType<TEvent extends SportEventModelType> = {
    state: BulkDeleteDialogState<TEvent>;
};

const Component = <TEvent extends SportEventModelType>({ state }: PropsType<TEvent>): JSX.Element => {
    const [showConfirm, setShowConfirm] = useState(false);

    const handleClose = (): void => {
        setShowConfirm(false);
        state.closeDialog();
    };

    const handleTryDelete = async (): Promise<void> => {
        if (showConfirm === false) {
            setShowConfirm(true);
        } else {
            await state.handleBulkDeleteWithDialog();
            handleClose();
        }
    };

    const handleCancel = (): void => {
        if (showConfirm === true) {
            setShowConfirm(false);
        } else {
            handleClose();
        }
    };

    const renderTable = (): JSX.Element => {
        if (state.data.length === 0) {
            return (
                <div className='w-[590px] h-full flex items-center justify-center'>{`You don't have any events to delete`}</div>
            );
        }

        return (
            <>
                <div className='my-6'>
                    <DatePicker
                        buttonText='Start Date'
                        selectedDate={state.selectedDate ?? undefined}
                        onDateSelect={(date) => state.setSelectedDate(date)}
                    />
                </div>

                <Table>
                    <TableHeader>
                        <TableRow className='bg-[#F4F4F5] [&_th]:text-[#0F172A] border-none [&_th]:text-[14px] [&_th]:font-normal'>
                            <TableHead className='rounded-l-[6px] py-2 w-[70px] max-w-[90px]'>
                                <div
                                    className='cursor-pointer flex items-center gap-2.5'
                                    onClick={() => state.setSortConfig('id')}
                                >
                                    ID
                                    <ArrowUpDownIcon className='size-4' />
                                </div>
                            </TableHead>
                            <TableHead className='py-2 w-[70px] max-w-[70px]'>
                                <div
                                    className='cursor-pointer flex items-center gap-2.5'
                                    onClick={() => state.setSortConfig('sport')}
                                >
                                    Sport
                                    <ArrowUpDownIcon className='size-4' />
                                </div>
                            </TableHead>
                            <TableHead className='py-2 w-[136px] max-w-[136px]'>
                                <div
                                    className='cursor-pointer flex items-center gap-2.5'
                                    onClick={() => state.setSortConfig('competition')}
                                >
                                    Competition
                                    <ArrowUpDownIcon className='size-4' />
                                </div>
                            </TableHead>
                            <TableHead className='py-2 w-[136px] max-w-[136px]'>
                                <div
                                    className='cursor-pointer flex items-center gap-2.5'
                                    onClick={() => state.setSortConfig('name')}
                                >
                                    Event
                                    <ArrowUpDownIcon className='size-4' />
                                </div>
                            </TableHead>
                            <TableHead className='py-2 w-[136px] max-w-[136px]'>
                                <div
                                    className='cursor-pointer flex items-center gap-2.5'
                                    onClick={() => state.setSortConfig('startTime')}
                                >
                                    Date
                                    <ArrowUpDownIcon className='size-4' />
                                </div>
                            </TableHead>
                            <TableHead className='rounded-r-[6px] w-8 max-w-8 py-2'>
                                <div className='flex items-center justify-center'>
                                    <Checkbox
                                        checked={state.isHeaderChecked}
                                        onChange={state.handleSelectAll}
                                        indeterminate={state.isIndeterminate}
                                    />
                                </div>
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {state.paginatedData.map((event) => (
                            <TableRow
                                key={event.id}
                                className='[&_td]:text-[#0F172A] h-[72px] border-t-0 border-x-0 border-b border-solid border-[#E4E4E7] [&_td]:font-medium [&_td]:text-[14px]'
                            >
                                <TableCell className='truncate w-[70px] max-w-[90px]'>{event.eventId}</TableCell>
                                <TableCell className='truncate w-[70px] max-w-[70px]'>{event.sport}</TableCell>
                                <TableCell className='truncate w-[136px] max-w-[136px]'>{event.competition}</TableCell>
                                <TableCell className='truncate w-[136px] max-w-[136px]'>{event.name}</TableCell>
                                <TableCell className='truncate w-[136px] max-w-[136px]'>
                                    {moment(event.startTime).format('MM/DD/YYYY, HH:mm')}
                                </TableCell>
                                <TableCell className='w-8 max-w-8'>
                                    <div className='flex items-center justify-center'>
                                        <Checkbox
                                            checked={state.isEventSelected(event.id)}
                                            onChange={() => state.toggleEventSelection(event.id)}
                                        />
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </>
        );
    };

    const renderConfirm = (): JSX.Element => {
        const text =
            state.selectedEventIds.size === 1
                ? 'Are you sure you want to delete this event?'
                : `Are you sure you want to delete selected ${state.selectedEventIds.size} events?`;
        return (
            <div>
                <p>{text}</p>
            </div>
        );
    };

    return (
        <Dialog onClickOutside={handleClose}>
            <DialogHeader>
                <DialogTitle data-test='dialog-title'>Bulk Delete</DialogTitle>
            </DialogHeader>
            {showConfirm === true ? <div>{renderConfirm()}</div> : <div className='h-[450px]'>{renderTable()}</div>}
            <DialogFooter
                data-test='dialog-footer'
                className='w-full'
            >
                <div className='flex flex-col gap-4'>
                    {showConfirm === false && <PaginationTable config={state.paginationConfig} />}
                    <div className='flex gap-4 justify-end'>
                        <Button
                            variant='outline'
                            onClick={handleCancel}
                            data-test='dialog-cancel-button'
                        >
                            Cancel
                        </Button>

                        {state.data.length > 0 && (
                            <Button
                                disabled={state.selectedEventIds.size === 0}
                                onClick={handleTryDelete}
                                variant='destructive'
                                data-test='dialog-delete-button'
                            >
                                Delete
                            </Button>
                        )}
                    </div>
                </div>
            </DialogFooter>
        </Dialog>
    );
};

export const BulkDeleteDialog = observer('BulkDeleteDialog', Component);
